import classNames from 'classnames';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import { useRightPanel } from '../app/layout/RightPanel';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import LargeTooltip from '../components/LargeTooltip';
import Menu, { MenuItem, MenuSeparator } from '../components/Menu';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import commentIcon from '../icons/non-prefilled/16x16/comment.svg';
import emailIcon from '../icons/non-prefilled/16x16/email.svg';
import faqIcon from '../icons/non-prefilled/16x16/faq.svg';
import helpIcon from '../icons/non-prefilled/16x16/help.svg';
import listCheckedIcon from '../icons/non-prefilled/16x16/listChecked.svg';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import shortcutIcon from '../icons/non-prefilled/16x16/shortcut.svg';
import universityIcon from '../icons/non-prefilled/16x16/university.svg';
import TutorialListModal from '../onboarding/tutorial/TutorialListModal';
import { UIActions } from '../ui/uiSlice';
import ReferralMenuItem from '../user/ReferralMenuItem';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import addTestId from '../utils/addTestId';
import intercomFacade from '../utils/intercomFacade';
import Link from '../utils/router/Link';
import useShortcut from '../utils/useShortcut';
import * as styles from './HelpMenu.module.scss';
import SearchOptionsModal from './SearchOptionsModal';
import TutorialMenuItem from './TutorialMenuItem';
import KeyboardShortcutsPanel from './shortcuts/KeyboardShortcutsPanel';

type MessageSupportMenuItemProps = {
  wrap?: React.ComponentProps<typeof MenuItem>['wrap'];
  noIcon?: boolean;
  fallbackToEmail?: boolean;
};
export const MessageSupportMenuItem = ({
  wrap,
  noIcon,
  fallbackToEmail,
}: MessageSupportMenuItemProps) => {
  const isIntercomDisabled = intercomFacade.hasError;

  if (isIntercomDisabled && fallbackToEmail) {
    return <EmailSupportMenuItem wrap={wrap} noIcon={noIcon} />;
  }

  return (
    <LargeTooltip
      placement="left"
      content={
        isIntercomDisabled
          ? {
              paragraph: (
                <>
                  Unable to load messaging, possibly due to Adblock. You can
                  also use our{' '}
                  <Link to="https://go.slab.com/slab-contact-us" newTab>
                    support form
                  </Link>{' '}
                  to get help.
                </>
              ),
            }
          : null
      }
    >
      <div>
        <MenuItem
          disabled={isIntercomDisabled}
          icon={noIcon ? null : commentIcon}
          onClick={() => {
            window.Intercom?.('show');
          }}
          wrap={wrap}
        >
          Message support
        </MenuItem>
      </div>
    </LargeTooltip>
  );
};

type EmailSupportMenuItemProps = {
  wrap?: React.ComponentProps<typeof MenuItem>['wrap'];
  noIcon?: boolean;
};

const EmailSupportMenuItem = ({ wrap, noIcon }: EmailSupportMenuItemProps) => (
  <MenuItem
    icon={noIcon ? null : emailIcon}
    to="mailto:support@slab.com?subject=Slab%20Product%20Support"
    newTab
    wrap={wrap}
  >
    Email support
  </MenuItem>
);

type HelpDocumentationMenuItemProps = {
  wrap?: React.ComponentProps<typeof MenuItem>['wrap'];
  noIcon?: boolean;
};
export const HelpDocumentationMenuItem = ({
  wrap,
  noIcon,
}: HelpDocumentationMenuItemProps) => (
  <MenuItem
    icon={noIcon ? null : faqIcon}
    to="https://go.slab.com/help-center"
    newTab
    wrap={wrap}
  >
    Help documentation
  </MenuItem>
);

type ProductUpdatesMenuItemProps = {
  wrap?: React.ComponentProps<typeof MenuItem>['wrap'];
  noIcon?: boolean;
};
export const ProductUpdatesMenuItem = ({
  wrap,
  noIcon,
}: ProductUpdatesMenuItemProps) => (
  <MenuItem
    icon={noIcon ? null : listCheckedIcon}
    to="https://go.slab.com/product-updates"
    newTab
    wrap={wrap}
  >
    Product updates
  </MenuItem>
);

interface HelpMenuProps {
  appearance: 'internal' | 'public';
}

const HelpMenu = ({ appearance }: HelpMenuProps) => {
  const dispatch = useAppDispatch();
  const isInTutorial = useAppSelector(
    (state) => !!state.onboarding.activeTutorial,
  );
  const { openRightPanel } = useRightPanel();
  const isNonVisitorUser = useIsNonVisitorUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isShowingTutorialList, setIsShowingTutorialList] = useState(false);

  const showSearchOptions = () =>
    dispatch(UIActions.openModal(SearchOptionsModal));

  const toggleTutorialList = () =>
    setIsShowingTutorialList(!isShowingTutorialList);

  useShortcut(
    'mod+/',
    (e) => {
      openRightPanel(<KeyboardShortcutsPanel />);
      e.preventDefault();
    },
    { global: true, skip: !isNonVisitorUser },
  );

  return (
    <div
      data-no-print
      className={classNames(styles.container, {
        [styles.inTutorial]: isInTutorial,
        [styles.showTutorialListModal]: isShowingTutorialList,
        [styles.isInternalView]: appearance === 'internal',
        [styles.isPublicView]: appearance === 'public',
      })}
    >
      {isNonVisitorUser &&
        isShowingTutorialList &&
        createPortal(
          <div className={styles.tutorialListModal}>
            <TutorialListModal onDismiss={toggleTutorialList} />
          </div>,
          document.body,
        )}
      <ClickOutsideHandler
        onClickOutside={() => {
          setIsOpen(false);
        }}
      >
        <PopOverPrimitive
          trigger="click"
          placement="top-end"
          open={isOpen}
          onOpenChange={setIsOpen}
          offset={4}
        >
          <PopOverPrimitive.Trigger>
            <Button
              className={styles.triggerButton}
              onClick={() => {
                setIsOpen((value) => !value);
              }}
              aria-label="Help menu"
            >
              <SVGIcon
                size={appearance === 'public' ? 16 : 12}
                {...addTestId('helpMenuTrigger')}
              >
                {helpIcon}
              </SVGIcon>
            </Button>
          </PopOverPrimitive.Trigger>
          <PopOverPrimitive.Floating>
            <PopOverPrimitive.Content onClick={() => setIsOpen(false)}>
              <Menu>
                <MessageSupportMenuItem />
                <EmailSupportMenuItem />
                <HelpDocumentationMenuItem />
                {isNonVisitorUser && (
                  <>
                    <TutorialMenuItem onClick={toggleTutorialList} />
                    <MenuSeparator />
                    <MenuItem
                      icon={shortcutIcon}
                      onClick={() => {
                        openRightPanel(<KeyboardShortcutsPanel />);
                      }}
                    >
                      Keyboard shortcuts
                    </MenuItem>
                    <MenuItem icon={searchIcon} onClick={showSearchOptions}>
                      Search shortcuts
                    </MenuItem>
                    <MenuSeparator />
                    <MenuItem
                      icon={universityIcon}
                      to="https://go.slab.com/slab-university"
                      newTab
                    >
                      Slab University
                    </MenuItem>
                    <ReferralMenuItem />
                    <ProductUpdatesMenuItem />
                  </>
                )}
              </Menu>
            </PopOverPrimitive.Content>
          </PopOverPrimitive.Floating>
        </PopOverPrimitive>
      </ClickOutsideHandler>
    </div>
  );
};

export default HelpMenu;
